import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import ApiService from 'services/apiService';
import {
    ParticipantStatusReportResponse,
    ParticipantStatusRow,
    ParticipantStatusChartValue,
    Program,
} from '../types/ParticipantStatusData';

interface ParticipantsFilter {
    schoolYear: number;
    programId?: number;
}

interface ParticipantsDataContextValue {
    // Data
    data: ParticipantStatusReportResponse | null;
    loading: boolean;
    error: string | null;
    chartData: ParticipantStatusChartValue[];
    tableData: ParticipantStatusRow[];

    // Filter state
    filter: ParticipantsFilter;
    updateFilter: (updates: Partial<ParticipantsFilter>) => void;

    // Lookup data
    programs: Program[];
    schoolYears: number[];
    programsLoading: boolean;
}

const ParticipantsDataContext = createContext<ParticipantsDataContextValue | undefined>(undefined);

interface ParticipantsDataProviderProps {
    children: ReactNode;
}

export const ParticipantsDataProvider: React.FC<ParticipantsDataProviderProps> = ({ children }) => {
    const currentYear = new Date().getFullYear();

    // Filter state
    const [filter, setFilter] = useState<ParticipantsFilter>({
        schoolYear: currentYear,
        programId: undefined,
    });

    // Data state
    const [data, setData] = useState<ParticipantStatusReportResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Lookup state
    const [programs, setPrograms] = useState<Program[]>([]);
    const [programsLoading, setProgramsLoading] = useState<boolean>(true);

    // Generate school years (last 5 years)
    const schoolYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

    // Fetch programs from StudentFilterResource on mount
    useEffect(() => {
        const fetchPrograms = async () => {
            setProgramsLoading(true);
            try {
                const resource = await ApiService.students.getFilterResource();
                setPrograms(resource.programs || []);
            } catch (err) {
                console.error('Error fetching programs:', err);
                setPrograms([]);
            } finally {
                setProgramsLoading(false);
            }
        };

        fetchPrograms();
    }, []);

    // Fetch data when filter changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await ApiService.studentReports.getParticipantStatusReport({
                    schoolYear: filter.schoolYear,
                    programId: filter.programId,
                });
                setData(response);
            } catch (err: any) {
                setError(err?.message || 'Failed to load participant data');
                console.error('Error fetching participant status data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [filter.schoolYear, filter.programId]);

    // Update filter
    const updateFilter = useCallback((updates: Partial<ParticipantsFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    // Transform data for chart
    const chartData: ParticipantStatusChartValue[] = data?.rows?.map(row => ({
        label: row.participantStatusName || 'Unknown',
        value: row.participantCount
    })) || [];

    // Table data is just the rows from the response
    const tableData: ParticipantStatusRow[] = data?.rows || [];

    return (
        <ParticipantsDataContext.Provider
            value={{
                data,
                loading,
                error,
                chartData,
                tableData,
                filter,
                updateFilter,
                programs,
                schoolYears,
                programsLoading,
            }}
        >
            {children}
        </ParticipantsDataContext.Provider>
    );
};

export const useParticipantsData = () => {
    const context = useContext(ParticipantsDataContext);
    if (!context) {
        throw new Error('useParticipantsData must be used within ParticipantsDataProvider');
    }
    return context;
};